import dayjs from "dayjs"
import { dayJstoDateOnly } from "../../../../utils/dayjs-to-date-only"
import { CalendarGrid } from "./grid"
import { CalendarTable } from "./table"
import {
  CalendarAppointment,
  eAppointmentCountingMethod,
} from "../../../../@client/Theasoft_ApiServer_Calendar"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { CalendarPageView } from "../../my/calendar/-components/CalendarPage"
import { match } from "ts-pattern"
import { CalendarWeekList } from "./CalendarWeekList"

export interface RenderCalendarProps {
  startDate: Date
  endDate: Date
  eventsByDate: Map<string, CalendarAppointment[]>
  statusByDate: Map<string, CalendarAppointment[]>
  holidaysByDate: Set<string>
  schoolHolidaysByDate: Set<string>
}

interface Props {
  startDate: Date
  endDate: Date
  appointmentCountMethod?: eAppointmentCountingMethod
  view: CalendarPageView
  weekListStartDate?: Date
}

export function Calendar({
  endDate,
  startDate,
  view,
  appointmentCountMethod = "NoCount",
  weekListStartDate = new Date(),
}: Props) {
  const start = dayjs(startDate).startOf("month")
  const end = dayjs(endDate).endOf("month")
  const dataStart = start.startOf("isoWeek")
  const dataEnd = end.endOf("isoWeek")

  const { data: holidays } = useSuspenseQueryGet(
    "/api/Holiday/GetHolidaysForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
    }
  )

  const { data: schoolHolidays } = useSuspenseQueryGet(
    "/api/SchoolHoliday/GetSchoolHolidaysForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
    }
  )

  const { data: allEvents } = useSuspenseQueryGet(
    "/api/Calendar/GetMyEventsForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
      method: appointmentCountMethod,
      withPossibleCasts: true,
      onlyReleasedDuties: false,
      withPersStatus: false,
    }
  )

  const { data: allStatus } = useSuspenseQueryGet(
    "/api/Calendar/GetMyStatusForDateRange",
    {
      endDate: dayJstoDateOnly(dataEnd),
      startDate: dayJstoDateOnly(dataStart),
    }
  )

  const eventsByDate = new Map(
    allEvents.map(([date, events]) => [
      dayjs(date).format("YYYY-MM-DD"),
      events,
    ])
  )

  const statusByDate = new Map(
    allStatus.map(([date, events]) => [
      dayjs(date).format("YYYY-MM-DD"),
      events,
    ])
  )

  const holidaysByDate = new Set(
    holidays.map((holiday) => dayjs(holiday.date).format("YYYY-MM-DD"))
  )

  const schoolHolidaysByDate = schoolHolidays.reduce((set, holiday) => {
    const start = dayjs(holiday.startDate)
    const end = dayjs(holiday.endDate)

    for (
      let d = start;
      d.isBefore(end) || d.isSame(end, "day");
      d = d.add(1, "day")
    ) {
      set.add(d.format("YYYY-MM-DD"))
    }

    return set
  }, new Set<string>())

  const calendarComponent = match(view)
    .with("grid", () => (
      <CalendarGrid
        endDate={end.toDate()}
        startDate={start.toDate()}
        eventsByDate={eventsByDate}
        holidaysByDate={holidaysByDate}
        schoolHolidaysByDate={schoolHolidaysByDate}
        statusByDate={statusByDate}
      />
    ))
    .with("table", () => (
      <CalendarTable
        endDate={end.toDate()}
        startDate={start.toDate()}
        eventsByDate={eventsByDate}
        holidaysByDate={holidaysByDate}
        schoolHolidaysByDate={schoolHolidaysByDate}
        statusByDate={statusByDate}
      />
    ))
    .with("week-list", () => <CalendarWeekList startDate={weekListStartDate} />)
    .exhaustive()

  return calendarComponent
}
